import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { asList, getJson, money, periodLabel, shortDate } from '../helpers';
import { CLARET_REPORTS, reportPath } from '../reports';

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

function ReportShell({ projectVuid, projectName, report, children, periods, selectedPeriod, onPeriodChange, showPeriod }) {
  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            {report.name}
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            {report.name}
          </>
        )}
      </div>
      <div className="pc-title-row">
        <div>
          <h1>{report.name}</h1>
          <p className="pc-muted" style={{ margin: '4px 0 0' }}>{report.description}</p>
        </div>
      </div>
      {showPeriod ? (
        <div className="pc-toolbar">
          <label className="pc-toolbar-label">
            As of period
            <select value={selectedPeriod} onChange={(event) => onPeriodChange(event.target.value)}>
              {periods.length === 0 ? <option value="">No periods</option> : null}
              {periods.map((period) => (
                <option key={period.vuid} value={period.vuid}>
                  {periodLabel(period)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function DataTable({ columns, rows, empty }) {
  if (!rows.length) {
    return <div className="pc-card"><div className="pc-empty">{empty}</div></div>;
  }
  return (
    <div className="pc-card pc-report-card">
      <div className="pc-table-scroll">
        <table className="pc-table pc-report">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.right ? 'pc-right' : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || index}>
                {columns.map((column) => (
                  <td key={column.key} className={column.right ? 'pc-right' : undefined}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ClaretReportPage({ reportId, projectVuid }) {
  const report = CLARET_REPORTS.find((item) => item.id === reportId) || {
    id: reportId,
    name: 'Report',
    description: '',
  };
  const [projectName, setProjectName] = useState('');
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const needsPeriod = reportId !== 'commitments';

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getJson('/api/accounting-periods').catch(() => []),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ]).then(([periodData, project]) => {
      if (cancelled) return;
      const sorted = sortPeriods(asList(periodData));
      setPeriods(sorted);
      const openPeriod = sorted.find((period) => period.status === 'open');
      setSelectedPeriod((openPeriod || sorted[0] || {}).vuid || '');
      if (project?.project_name) setProjectName(project.project_name);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Unable to load report setup');
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    if (needsPeriod && !selectedPeriod) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const load = async () => {
      if (reportId === 'commitments') {
        const params = projectVuid ? { project_vuid: projectVuid } : {};
        return asList(await getJson('/api/commitments-report', params));
      }
      if (reportId === 'journal-entries') {
        const params = { accounting_period_vuid: selectedPeriod };
        if (projectVuid) params.project_vuid = projectVuid;
        return asList(await getJson('/api/journal-entries', params));
      }
      if (reportId === 'unposted') {
        const data = await getJson(`/api/unposted-transactions/${selectedPeriod}`);
        const list = asList(data?.unposted_transactions || data);
        return projectVuid ? list.filter((row) => row.project_vuid === projectVuid) : list;
      }
      if (reportId === 'audit') {
        const params = {};
        if (selectedPeriod) params.accounting_period_vuid = selectedPeriod;
        if (projectVuid) params.project_vuid = projectVuid;
        const data = await getJson('/api/accounting-audit-report', params);
        return asList(data?.posted_records || data);
      }
      if (reportId === 'general-ledger') {
        const data = await getJson('/api/general-ledger-report', {
          accounting_period_vuid: selectedPeriod,
        });
        return asList(data?.accounts).map((account) => ({
          id: account.account_vuid || account.vuid,
          account_number: account.account_number,
          account_name: account.account_name,
          total_debits: account.total_debits,
          total_credits: account.total_credits,
        }));
      }
      return [];
    };
    load()
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || `Unable to load ${report.name.toLowerCase()}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, selectedPeriod, projectVuid, needsPeriod, report.name]);

  const columns = useMemo(() => {
    if (reportId === 'commitments') {
      return [
        { key: 'commitment', label: 'Commitment', render: (row) => (
          <>
            {row.commitment_number || '—'}
            <div className="pc-sub">{row.description}</div>
            {!projectVuid && row.project ? (
              <div className="pc-sub">{row.project.project_number} — {row.project.project_name}</div>
            ) : null}
          </>
        ) },
        { key: 'vendor', label: 'Vendor', render: (row) => row.vendor?.vendor_name || row.vendor_name || '—' },
        { key: 'original_amount', label: 'Original Amount', right: true, render: (row) => money(row.original_amount) },
        { key: 'change_orders_amount', label: 'Change Orders', right: true, render: (row) => money(row.change_orders_amount) },
        { key: 'current_amount', label: 'Current Amount', right: true, render: (row) => money(row.current_amount) },
        { key: 'invoiced_amount', label: 'Invoiced', right: true, render: (row) => money(row.invoiced_amount) },
        { key: 'remaining_amount', label: 'Remaining', right: true, render: (row) => money(row.remaining_amount) },
        { key: 'status', label: 'Status' },
      ];
    }
    if (reportId === 'journal-entries') {
      return [
        { key: 'journal_number', label: '#' },
        { key: 'entry_date', label: 'Date', render: (row) => shortDate(row.entry_date || row.posted_at) },
        { key: 'description', label: 'Description' },
        { key: 'project', label: 'Project', render: (row) => row.project?.project_name || row.project_name || '—' },
        { key: 'reference_type', label: 'Type', render: (row) => row.reference_type || row.transaction_type || '—' },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => {
          const fromLines = (row.line_items || []).reduce((sum, line) => sum + Number(line.debit_amount || 0), 0);
          return money(fromLines || row.total_amount);
        } },
      ];
    }
    if (reportId === 'unposted') {
      return [
        { key: 'transaction_type', label: 'Type', render: (row) => row.transaction_type || '—' },
        { key: 'reference_number', label: 'Reference', render: (row) => row.reference_number || '—' },
        { key: 'description', label: 'Description' },
        { key: 'project_name', label: 'Project', render: (row) => row.project_name || '—' },
        { key: 'vendor_name', label: 'Vendor', render: (row) => row.vendor_name || '—' },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => money(row.total_amount) },
        { key: 'created_at', label: 'Created', render: (row) => shortDate(row.created_at) },
      ];
    }
    if (reportId === 'audit') {
      return [
        { key: 'posted_at', label: 'Posted', render: (row) => shortDate(row.posted_at) },
        { key: 'transaction_type', label: 'Type', render: (row) => row.transaction_type || '—' },
        { key: 'reference_number', label: 'Reference', render: (row) => row.reference_number || '—' },
        { key: 'project', label: 'Project', render: (row) => (
          row.project ? `${row.project.project_number} — ${row.project.project_name}` : (row.project_name || '—')
        ) },
        { key: 'period', label: 'Period', render: (row) => (
          row.accounting_period ? `${row.accounting_period.month}/${row.accounting_period.year}` : '—'
        ) },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => money(row.total_amount) },
        { key: 'sync', label: 'Sync', render: (row) => (row.is_synced ? `Synced (${row.sync_count || 1})` : 'Not synced') },
      ];
    }
    return [
      { key: 'account_number', label: 'Account' },
      { key: 'account_name', label: 'Account Name' },
      { key: 'total_debits', label: 'Total Debits', right: true, render: (row) => money(row.total_debits) },
      { key: 'total_credits', label: 'Total Credits', right: true, render: (row) => money(row.total_credits) },
    ];
  }, [reportId, projectVuid]);

  return (
    <ReportShell
      projectVuid={projectVuid}
      projectName={projectName}
      report={report}
      periods={periods}
      selectedPeriod={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
      showPeriod={needsPeriod}
    >
      {loading ? (
        <div className="pc-card"><div className="pc-empty">Loading {report.name.toLowerCase()}…</div></div>
      ) : error ? (
        <div className="pc-card"><div className="pc-empty">{error}</div></div>
      ) : (
        <DataTable columns={columns} rows={rows} empty={`No ${report.name.toLowerCase()} data for this period.`} />
      )}
    </ReportShell>
  );
}
